/**
 * FM Checkout Upsell Modal
 * ========================
 * Intercepts standard checkout button clicks and shows an upsell modal.
 * Does NOT intercept accelerated checkout (Shop Pay, Apple Pay, Google Pay).
 *
 * Usage:
 * This script is automatically loaded by the fm-checkout-upsell-modal section.
 * Configure upsell products in Theme Editor.
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG_ELEMENT_ID = 'fm-upsell-config';
  const MODAL_ID = 'fm-upsell-modal';

  // Selectors for checkout interception
  const CART_FORM_SELECTOR = 'form[action^="/cart"]';
  const CHECKOUT_BUTTON_SELECTOR = 'button[name="checkout"], input[name="checkout"]';
  const CHECKOUT_LINK_SELECTOR = 'a[href="/checkout"], a[href^="/checkout?"]';

  // Drawer selectors (various theme implementations)
  const DRAWER_SELECTORS = [
    '.cart-drawer',
    '[data-cart-drawer]',
    '#CartDrawer',
    'cart-drawer',
    'drawer-cart',
    '.drawer--cart',
    '#cart-drawer',
    '.mini-cart',
    '.cart-notification'
  ].join(', ');

  // Selectors to EXCLUDE (accelerated checkout)
  const EXCLUDED_SELECTORS = [
    '.shopify-payment-button',
    '.shopify-payment-button__button',
    '[data-shopify-buttoncontainer]',
    '.shopify-payment-button__more-options',
    '.additional-checkout-buttons',
    'shopify-accelerated-checkout',
    'shopify-payment-button',
    '[data-accelerated-checkout]'
  ].join(', ');

  // Cart protection constants
  const CART_SNAPSHOT_KEY = 'fm_upsell_cart_snapshot';
  const CART_POLL_INTERVAL = 2500; // ms
  const SNAPSHOT_EXPIRY_MS = 30 * 60 * 1000; // 30 minut

  // Timer persistence constants
  const TIMER_START_KEY = 'fm_upsell_timer_start';
  const MINIMUM_TIMER_SECONDS = 180; // 3 minuty - minimální doba zobrazení

  // Fraud prevention constants
  const MODAL_SHOWN_KEY = 'fm_upsell_modal_shown';
  const CHECKOUT_INTENT_KEY = 'fm_upsell_checkout_intent';
  const CHECKOUT_REFERRER_PATTERN = /checkout/i;

  // BFCache and mobile back navigation protection constants
  const CHECKOUT_NAV_TIMESTAMP_KEY = 'fm_upsell_checkout_nav_ts';
  const CHECKOUT_DETECTION_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
  const UPSELL_ADDED_TIMESTAMP_KEY = 'fm_upsell_added_ts';
  const MAX_UPSELL_SESSION_MS = 30 * 60 * 1000; // 30 minutes

  // Warning modal constants
  const WARNING_MODAL_ID = 'fm-upsell-warning-modal';

  // State
  let config = null;
  let modal = null;
  let isLoading = false;
  let checkoutIntentFlag = false;
  let cartPollTimer = null;
  let isRemovingItems = false;

  // Countdown timer state
  let countdownInterval = null;
  let countdownSeconds = 0;

  // Warning modal state
  let warningModal = null;
  let pendingCartAction = null;
  let originalFetch = window.fetch;

  /**
   * Initialize the upsell modal
   */
  function init() {
    // Load configuration
    config = loadConfig();
    if (!config || !config.enabled || !config.items || config.items.length === 0) {
      return; // Modal disabled or no items configured
    }

    // Get modal element
    modal = document.getElementById(MODAL_ID);
    if (!modal) {
      console.warn('[FM Upsell] Modal element not found');
      return;
    }

    // ====== BFCACHE AND MOBILE BACK NAVIGATION PROTECTION ======
    // Register event listeners for bfcache detection (always, even if modal was shown)
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChangeForFraud);

    // Validate upsell session integrity on every page load
    validateUpsellSession();
    // ===========================================================

    // Check if returning from checkout - show checkout return modal
    if (hasReturnedFromCheckout()) {
      showCheckoutReturnModal();
      return;
    }

    // FRAUD PREVENTION: If modal was already shown, don't intercept checkout
    if (hasModalBeenShown()) {
      // Still initialize cart protection for any existing upsell items
      initCartProtection();
      return;
    }

    // Setup event listeners
    setupCheckoutIntercept();
    setupModalEvents();
    setupCardEvents();

    // Initialize cart protection (monitoring)
    initCartProtection();

    // Initialize warning modal for cart modification protection
    initWarningModal();
  }

  /**
   * Load configuration from JSON script tag
   */
  function loadConfig() {
    const configEl = document.getElementById(CONFIG_ELEMENT_ID);
    if (!configEl) {
      return null;
    }

    try {
      return JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('[FM Upsell] Failed to parse config:', e);
      return null;
    }
  }

  /**
   * Setup checkout interception
   */
  function setupCheckoutIntercept() {
    // Intercept cart form submissions
    document.addEventListener('submit', handleFormSubmit, true);

    // Intercept checkout button clicks (for fallback and drawer support)
    document.addEventListener('click', handleCheckoutClick, true);
  }

  /**
   * Handle form submission
   */
  function handleFormSubmit(e) {
    const form = e.target;

    // Check if it's a cart form
    if (!form.matches || !form.matches(CART_FORM_SELECTOR)) {
      return;
    }

    // Check if submitter is checkout button
    const submitter = e.submitter;
    if (submitter && submitter.name === 'checkout') {
      // Check if it's an excluded (accelerated) button
      if (isExcludedElement(submitter)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      openModal();
      return;
    }

    // Fallback: check our intent flag
    if (checkoutIntentFlag) {
      checkoutIntentFlag = false;
      e.preventDefault();
      e.stopPropagation();
      openModal();
    }
  }

  /**
   * Handle checkout button/link clicks
   */
  function handleCheckoutClick(e) {
    const target = e.target;
    const clickedElement = target.closest(CHECKOUT_BUTTON_SELECTOR) ||
                           target.closest(CHECKOUT_LINK_SELECTOR);

    if (!clickedElement) {
      return;
    }

    // Check if it's an excluded (accelerated) button
    if (isExcludedElement(clickedElement)) {
      return;
    }

    // Check if it's inside a drawer
    const inDrawer = clickedElement.closest(DRAWER_SELECTORS);

    // For buttons inside forms, set intent flag (form submit handler will catch it)
    const parentForm = clickedElement.closest(CART_FORM_SELECTOR);
    if (parentForm && clickedElement.matches(CHECKOUT_BUTTON_SELECTOR)) {
      // If it's a button (not link), let the form submit handle it
      if (clickedElement.tagName === 'BUTTON' || clickedElement.tagName === 'INPUT') {
        checkoutIntentFlag = true;
        return;
      }
    }

    // For links or drawer checkout elements
    if (clickedElement.matches(CHECKOUT_LINK_SELECTOR) || inDrawer) {
      e.preventDefault();
      e.stopPropagation();
      openModal();
    }
  }

  /**
   * Check if element should be excluded from interception
   */
  function isExcludedElement(element) {
    // Check the element itself
    if (element.matches && element.matches(EXCLUDED_SELECTORS)) {
      return true;
    }

    // Check parent elements
    if (element.closest && element.closest(EXCLUDED_SELECTORS)) {
      return true;
    }

    return false;
  }

  /**
   * Setup modal events
   */
  function setupModalEvents() {
    // Decline button - go directly to checkout (no upsell accepted, no validation needed)
    const declineBtn = modal.querySelector('[data-fm-decline]');
    if (declineBtn) {
      declineBtn.addEventListener('click', function() {
        closeModal();
        // FRAUD PREVENTION: Set checkout intent flag
        setCheckoutIntent();
        // Clear timer start timestamp
        clearTimerStart();
        // Přímý redirect - zákazník upsell nepřijal, není třeba validovat
        window.location.assign('/checkout');
      });
    }

    // Accept button - add items and go to checkout
    const acceptBtn = modal.querySelector('[data-fm-accept]');
    if (acceptBtn) {
      acceptBtn.addEventListener('click', handleAccept);
    }

    // Continue button (in error state)
    const continueBtn = modal.querySelector('[data-fm-continue]');
    if (continueBtn) {
      continueBtn.addEventListener('click', function() {
        closeModal();
        goToCheckout();
      });
    }
  }

  /**
   * Setup card checkbox events for visual feedback
   */
  function setupCardEvents() {
    const cards = modal.querySelectorAll('[data-fm-card]');
    cards.forEach(function(card) {
      const checkbox = card.querySelector('input[type="checkbox"]');
      if (checkbox) {
        // Initial state
        updateCardState(card, checkbox.checked);

        // On change
        checkbox.addEventListener('change', function() {
          updateCardState(card, checkbox.checked);
          updateCounter();
        });
      }
    });
  }

  /**
   * Update card visual state
   */
  function updateCardState(card, checked) {
    if (checked) {
      card.classList.add('fm-upsell__card--checked');
      createConfetti(card);
    } else {
      card.classList.remove('fm-upsell__card--checked');
    }
  }

  /**
   * Create confetti animation on card
   */
  function createConfetti(card) {
    var colors = ['#386641', '#4a7c59', '#6b9b7a', '#8db89c', '#fbbf24', '#f59e0b'];
    var symbols = ['●', '■', '▲', '★', '♦'];
    var confettiCount = 10;

    for (var i = 0; i < confettiCount; i++) {
      var confetti = document.createElement('span');
      confetti.className = 'fm-upsell__confetti';
      confetti.textContent = symbols[Math.floor(Math.random() * symbols.length)];
      confetti.style.color = colors[Math.floor(Math.random() * colors.length)];
      confetti.style.left = (20 + Math.random() * 60) + '%';
      confetti.style.top = '40%';
      confetti.style.animation = 'fm-upsell-confetti-fall 0.6s ease-out forwards';
      confetti.style.animationDelay = (Math.random() * 0.15) + 's';
      card.appendChild(confetti);

      // Remove confetti element after animation
      (function(el) {
        setTimeout(function() {
          if (el.parentNode) {
            el.parentNode.removeChild(el);
          }
        }, 800);
      })(confetti);
    }
  }

  /**
   * Update the selection counter display
   */
  function updateCounter() {
    const total = modal.querySelectorAll('input[name="fm-upsell-item"]:not(:disabled)').length;
    const checked = modal.querySelectorAll('input[name="fm-upsell-item"]:checked').length;

    // Desktop counter
    const currentEl = document.getElementById('fm-upsell-counter-current');
    const totalEl = document.getElementById('fm-upsell-counter-total');

    if (currentEl) currentEl.textContent = checked;
    if (totalEl) totalEl.textContent = total;

    // Mobile counter
    const mobileCurrentEl = modal.querySelector('.fm-upsell-counter-current-mobile');
    const mobileTotalEl = modal.querySelector('.fm-upsell-counter-total-mobile');

    if (mobileCurrentEl) mobileCurrentEl.textContent = checked;
    if (mobileTotalEl) mobileTotalEl.textContent = total;
  }

  // ============================================
  // COUNTDOWN TIMER FUNCTIONS
  // ============================================

  /**
   * Start the countdown timer
   * Timer persists across modal close/reopen with minimum guaranteed display time
   */
  function startCountdown() {
    // Stop any existing countdown
    stopCountdown();

    // Get duration from timer element (in minutes)
    var timerEl = document.getElementById('fm-upsell-timer');
    if (!timerEl) return;

    var durationMinutes = parseInt(timerEl.getAttribute('data-duration') || '5', 10);
    var totalDurationSeconds = durationMinutes * 60;

    // Get or set the first open timestamp
    var firstOpenTime = null;
    try {
      firstOpenTime = sessionStorage.getItem(TIMER_START_KEY);
    } catch (e) {
      // sessionStorage not available
    }

    if (!firstOpenTime) {
      // First time opening - save timestamp
      firstOpenTime = Date.now().toString();
      try {
        sessionStorage.setItem(TIMER_START_KEY, firstOpenTime);
      } catch (e) {
        // sessionStorage not available
      }
    }

    // Calculate elapsed time since first open
    var elapsedSeconds = Math.floor((Date.now() - parseInt(firstOpenTime, 10)) / 1000);
    var remainingSeconds = totalDurationSeconds - elapsedSeconds;

    // Apply minimum display time (don't show less than MINIMUM_TIMER_SECONDS)
    if (remainingSeconds < MINIMUM_TIMER_SECONDS && remainingSeconds > 0) {
      remainingSeconds = MINIMUM_TIMER_SECONDS;
    }

    // If time already fully expired, go to checkout immediately
    if (remainingSeconds <= 0) {
      onTimerExpired();
      return;
    }

    countdownSeconds = remainingSeconds;

    // Update display immediately
    updateTimerDisplay();

    // Start interval
    countdownInterval = setInterval(function() {
      countdownSeconds--;

      if (countdownSeconds <= 0) {
        // Timer expired - close modal and go to checkout
        stopCountdown();
        onTimerExpired();
      } else {
        updateTimerDisplay();
      }
    }, 1000);
  }

  /**
   * Stop the countdown timer
   */
  function stopCountdown() {
    if (countdownInterval) {
      clearInterval(countdownInterval);
      countdownInterval = null;
    }
  }

  /**
   * Clear the timer start timestamp (call on checkout or expiration)
   */
  function clearTimerStart() {
    try {
      sessionStorage.removeItem(TIMER_START_KEY);
    } catch (e) {
      // sessionStorage not available
    }
  }

  /**
   * Update the timer display
   */
  function updateTimerDisplay() {
    var timerEl = document.getElementById('fm-upsell-timer');
    var valueEl = document.getElementById('fm-upsell-timer-value');
    if (!valueEl) return;

    var minutes = Math.floor(countdownSeconds / 60);
    var seconds = countdownSeconds % 60;

    // Format as M:SS
    valueEl.textContent = minutes + ':' + (seconds < 10 ? '0' : '') + seconds;

    // Add urgent class when under 1 minute
    if (timerEl) {
      if (countdownSeconds < 60) {
        timerEl.classList.add('fm-upsell__timer--urgent');
      } else {
        timerEl.classList.remove('fm-upsell__timer--urgent');
      }
    }
  }

  /**
   * Handle timer expiration - close modal and go to checkout
   */
  function onTimerExpired() {
    closeModal();
    // FRAUD PREVENTION: Set checkout intent flag
    setCheckoutIntent();
    // Clear timer start timestamp
    clearTimerStart();
    // Go directly to checkout without adding upsell items
    window.location.assign('/checkout');
  }

  /**
   * Open the upsell modal
   */
  function openModal() {
    if (!modal) return;

    // FRAUD PREVENTION: Don't show modal if already shown this session
    if (hasModalBeenShown()) {
      // Go directly to checkout without showing modal
      goToCheckout();
      return;
    }

    // Mark modal as shown (before actually showing, for safety)
    markModalAsShown();

    // Reset to default state
    resetModalState();

    // Show modal
    modal.hidden = false;

    // Lock body scroll
    document.documentElement.classList.add('fm-upsell-open');

    // Start countdown timer
    startCountdown();

    // Focus management
    const firstCheckbox = modal.querySelector('input[type="checkbox"]:not(:disabled)');
    const title = modal.querySelector('#fm-upsell-title');

    if (title) {
      title.focus();
    } else if (firstCheckbox) {
      firstCheckbox.focus();
    }
  }

  /**
   * Close the upsell modal
   */
  function closeModal() {
    if (!modal) return;

    // Stop countdown timer
    stopCountdown();

    modal.hidden = true;
    document.documentElement.classList.remove('fm-upsell-open');
  }

  /**
   * Reset modal to default state
   */
  function resetModalState() {
    // Reset checkboxes to default states
    const checkboxes = modal.querySelectorAll('input[name="fm-upsell-item"]');
    checkboxes.forEach(function(checkbox, index) {
      const configItem = config.items[index];
      if (configItem && configItem.available) {
        checkbox.checked = configItem.defaultChecked;
        updateCardState(checkbox.closest('[data-fm-card]'), checkbox.checked);
      }
    });

    // Hide error
    const errorEl = modal.querySelector('#fm-upsell-error');
    if (errorEl) {
      errorEl.hidden = true;
    }

    // Reset loading state
    setLoadingState(false);

    // Update counter
    updateCounter();
  }

  /**
   * Handle accept button click
   */
  function handleAccept() {
    if (isLoading) return;

    // Get checked items
    const checkedItems = getCheckedItems();

    // If nothing checked, just go to checkout
    if (checkedItems.length === 0) {
      goToCheckout();
      return;
    }

    // First, fetch current cart and save snapshot, then add upsell items
    setLoadingState(true);

    fetchCurrentCart()
      .then(function(cart) {
        // Save snapshot of original cart items
        var snapshot = saveCartSnapshot(cart);

        // Add required variant IDs to each upsell item as properties
        var itemsWithProperties = checkedItems.map(function(item) {
          return {
            id: item.id,
            quantity: item.quantity,
            properties: {
              _fm_upsell: 'true',
              _fm_requires: JSON.stringify(snapshot.originalVariantIds)
            }
          };
        });

        return addItemsToCartWithProperties(itemsWithProperties);
      })
      .catch(function(error) {
        console.error('[FM Upsell] Error in accept flow:', error);
        showError();
        setLoadingState(false);
      });
  }

  /**
   * Get list of checked items
   */
  function getCheckedItems() {
    const items = [];
    const checkboxes = modal.querySelectorAll('input[name="fm-upsell-item"]:checked');

    checkboxes.forEach(function(checkbox) {
      items.push({
        id: parseInt(checkbox.value, 10),
        quantity: parseInt(checkbox.dataset.qty || '1', 10)
      });
    });

    return items;
  }

  /**
   * Add items to cart via Ajax (legacy, without properties)
   */
  function addItemsToCart(items) {
    setLoadingState(true);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: items })
    })
    .then(function(response) {
      if (!response.ok) {
        throw new Error('Failed to add items');
      }
      return response.json();
    })
    .then(function() {
      // Success - go to checkout
      closeModal();
      goToCheckout();
    })
    .catch(function(error) {
      console.error('[FM Upsell] Error adding items:', error);
      showError();
    })
    .finally(function() {
      setLoadingState(false);
    });
  }

  /**
   * Add items to cart with properties (for upsell protection)
   */
  function addItemsToCartWithProperties(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: items })
    })
    .then(function(response) {
      if (!response.ok) {
        throw new Error('Failed to add items');
      }
      return response.json();
    })
    .then(function() {
      // Save timestamp when upsells were added (for session expiration tracking)
      setUpsellAddedTimestamp();

      // Spustit monitoring NYNÍ - upsell byl úspěšně přidán
      startCartPolling();

      // Success - go to checkout (modal zůstává otevřený až do redirectu)
      goToCheckout();
    })
    .catch(function(error) {
      console.error('[FM Upsell] Error adding items:', error);
      showError();
      throw error; // Re-throw to be caught by handleAccept
    })
    .finally(function() {
      setLoadingState(false);
    });
  }

  /**
   * Set loading state on accept button
   */
  function setLoadingState(loading) {
    isLoading = loading;
    var acceptBtn = modal.querySelector('[data-fm-accept]');
    if (acceptBtn) {
      acceptBtn.disabled = loading;
      var textAccept = acceptBtn.getAttribute('data-text-accept') || 'Ano, přidat';
      var textAdding = acceptBtn.getAttribute('data-text-adding') || 'Přidávám...';
      if (loading) {
        acceptBtn.classList.add('fm-upsell__btn--loading');
        acceptBtn.textContent = textAdding;
      } else {
        acceptBtn.classList.remove('fm-upsell__btn--loading');
        acceptBtn.textContent = textAccept;
      }
    }
  }

  /**
   * Show error message
   */
  function showError() {
    const errorEl = modal.querySelector('#fm-upsell-error');
    if (errorEl) {
      errorEl.hidden = false;
    }
  }

  // ============================================
  // CART PROTECTION FUNCTIONS
  // ============================================

  /**
   * Fetch current cart data from Shopify API
   */
  function fetchCurrentCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    })
    .then(function(response) {
      if (!response.ok) throw new Error('Failed to fetch cart');
      return response.json();
    });
  }

  /**
   * Save snapshot of current cart (original items before upsell)
   */
  function saveCartSnapshot(cart) {
    var originalItems = cart.items
      .filter(function(item) {
        // Exclude items that are already upsell items
        return !(item.properties && item.properties._fm_upsell === 'true');
      })
      .map(function(item) {
        return {
          variant_id: item.variant_id,
          product_id: item.product_id,
          quantity: item.quantity,
          key: item.key
        };
      });

    var snapshot = {
      timestamp: Date.now(),
      originalItems: originalItems,
      originalVariantIds: originalItems.map(function(item) { return item.variant_id; })
    };

    try {
      sessionStorage.setItem(CART_SNAPSHOT_KEY, JSON.stringify(snapshot));
    } catch (e) {
      console.warn('[FM Upsell] Failed to save cart snapshot:', e);
    }

    return snapshot;
  }

  /**
   * Get saved cart snapshot (with expiration check)
   */
  function getCartSnapshot() {
    try {
      var data = sessionStorage.getItem(CART_SNAPSHOT_KEY);
      if (!data) return null;

      var snapshot = JSON.parse(data);

      // Kontrola expirace - snapshot starší než 30 minut je neplatný
      if (Date.now() - snapshot.timestamp > SNAPSHOT_EXPIRY_MS) {
        clearCartSnapshot();
        return null;
      }

      return snapshot;
    } catch (e) {
      return null;
    }
  }

  /**
   * Clear cart snapshot
   */
  function clearCartSnapshot() {
    try {
      sessionStorage.removeItem(CART_SNAPSHOT_KEY);
    } catch (e) {
      // Ignore
    }
  }

  /**
   * Initialize cart protection monitoring
   */
  function initCartProtection() {
    // Handle visibility changes (pause when tab hidden)
    document.addEventListener('visibilitychange', function() {
      if (document.hidden) {
        stopCartPolling();
      } else {
        // Spustit polling pouze pokud existuje platný snapshot
        if (getCartSnapshot()) {
          startCartPolling();
        }
      }
    });

    // Zkontrolovat, zda existuje snapshot z předchozí session
    // (zákazník mohl přijmout upsell, pak refreshnout stránku)
    if (getCartSnapshot()) {
      startCartPolling();
    }
    // Jinak polling NEspouštíme - spustí se až po přijetí upsellu
  }

  /**
   * Start cart polling
   */
  function startCartPolling() {
    if (cartPollTimer) return;

    cartPollTimer = setInterval(function() {
      checkCartAndValidate();
    }, CART_POLL_INTERVAL);

    // Also check immediately
    checkCartAndValidate();
  }

  /**
   * Stop cart polling
   */
  function stopCartPolling() {
    if (cartPollTimer) {
      clearInterval(cartPollTimer);
      cartPollTimer = null;
    }
  }

  /**
   * Check cart and validate upsell items
   */
  function checkCartAndValidate() {
    // Don't check while we're removing items
    if (isRemovingItems) return;

    var snapshot = getCartSnapshot();

    // If no snapshot, check for orphaned upsell items
    if (!snapshot) {
      fetchCurrentCart()
        .then(function(cart) {
          var orphanedUpsells = cart.items.filter(function(item) {
            return item.properties && item.properties._fm_upsell === 'true';
          });

          if (orphanedUpsells.length > 0) {
            // Orphaned upsells detected - remove them
            console.log('[FM Upsell] Detected orphaned upsell items - removing');
            removeOrphanedUpsellItems();
          }
        })
        .catch(function(error) {
          console.warn('[FM Upsell] Orphan check failed:', error);
        });
      return;
    }

    fetchCurrentCart()
      .then(function(cart) {
        validateUpsellItems(cart, snapshot);
      })
      .catch(function(error) {
        console.warn('[FM Upsell] Cart check failed:', error);
      });
  }

  /**
   * Validate upsell items against required original items
   */
  function validateUpsellItems(cart, snapshot) {
    // Pokud je košík prázdný, vyčistit snapshot a zastavit polling
    if (cart.items.length === 0) {
      clearCartSnapshot();
      stopCartPolling();
      return;
    }

    // Get current variant IDs in cart (excluding upsell items)
    var currentVariantIds = cart.items
      .filter(function(item) {
        return !(item.properties && item.properties._fm_upsell === 'true');
      })
      .map(function(item) { return item.variant_id; });

    // Check if ALL original items are still present
    var originalVariantIds = snapshot.originalVariantIds || [];
    var allOriginalPresent = originalVariantIds.every(function(variantId) {
      return currentVariantIds.indexOf(variantId) !== -1;
    });

    // If original items are missing, remove upsell items
    if (!allOriginalPresent && originalVariantIds.length > 0) {
      var upsellItems = cart.items.filter(function(item) {
        return item.properties && item.properties._fm_upsell === 'true';
      });

      if (upsellItems.length > 0) {
        removeInvalidUpsellItems(upsellItems);
      }
    }
  }

  /**
   * Remove invalid upsell items from cart
   */
  function removeInvalidUpsellItems(itemsToRemove) {
    if (isRemovingItems || itemsToRemove.length === 0) return;

    isRemovingItems = true;

    // Build updates object - set quantity to 0 for each item
    var updates = {};
    itemsToRemove.forEach(function(item) {
      updates[item.key] = 0;
    });

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates: updates })
    })
    .then(function(response) {
      if (!response.ok) throw new Error('Failed to remove items');
      return response.json();
    })
    .then(function() {
      // Clear all upsell-related session data
      clearCartSnapshot();
      clearUpsellAddedTimestamp();

      // Trigger cart update event for theme to refresh UI
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      // Try to refresh sidecart if present
      refreshSidecart();
    })
    .catch(function(error) {
      console.error('[FM Upsell] Failed to remove upsell items:', error);
    })
    .finally(function() {
      isRemovingItems = false;
    });
  }

  /**
   * Try to refresh sidecart UI (theme-specific)
   */
  function refreshSidecart() {
    // Dispatch common cart events that themes listen to
    var events = ['cart:build', 'cart:update', 'ajaxCart:refresh'];
    events.forEach(function(eventName) {
      document.dispatchEvent(new CustomEvent(eventName));
    });

    // For themes using fetch-based cart drawer, try to find and click refresh
    var refreshTriggers = document.querySelectorAll('[data-cart-refresh], .cart-drawer__refresh');
    refreshTriggers.forEach(function(el) { el.click(); });
  }

  /**
   * Navigate to checkout (with final validation and timeout)
   */
  function goToCheckout() {
    // FRAUD PREVENTION: Set checkout intent flag
    setCheckoutIntent();

    // Clear timer start timestamp (user is proceeding to checkout)
    clearTimerStart();

    var snapshot = getCartSnapshot();

    // If no snapshot or expired, just go to checkout
    if (!snapshot) {
      clearCartSnapshot();
      closeModal();
      window.location.assign('/checkout');
      return;
    }

    // Timeout - pokud validace trvá déle než 3s, jít na checkout
    // Zabráníme "zamrznutí" pro zákazníka
    var checkoutTimeout = setTimeout(function() {
      clearCartSnapshot();
      closeModal();
      window.location.assign('/checkout');
    }, 3000);

    // Final validation before checkout
    fetchCurrentCart()
      .then(function(cart) {
        clearTimeout(checkoutTimeout);

        var currentVariantIds = cart.items
          .filter(function(item) {
            return !(item.properties && item.properties._fm_upsell === 'true');
          })
          .map(function(item) { return item.variant_id; });

        var originalVariantIds = snapshot.originalVariantIds || [];
        var allOriginalPresent = originalVariantIds.every(function(variantId) {
          return currentVariantIds.indexOf(variantId) !== -1;
        });

        if (!allOriginalPresent && originalVariantIds.length > 0) {
          // Remove upsell items first, then go to checkout
          var upsellItems = cart.items.filter(function(item) {
            return item.properties && item.properties._fm_upsell === 'true';
          });

          if (upsellItems.length > 0) {
            removeInvalidUpsellItemsAndCheckout(upsellItems);
            return;
          }
        }

        // All good, clear snapshot and go to checkout
        clearCartSnapshot();
        closeModal();
        window.location.assign('/checkout');
      })
      .catch(function() {
        clearTimeout(checkoutTimeout);
        // On error, just go to checkout
        clearCartSnapshot();
        closeModal();
        window.location.assign('/checkout');
      });
  }

  /**
   * Remove invalid upsell items and then proceed to checkout
   */
  function removeInvalidUpsellItemsAndCheckout(itemsToRemove) {
    var updates = {};
    itemsToRemove.forEach(function(item) {
      updates[item.key] = 0;
    });

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates: updates })
    })
    .then(function() {
      // Clear all upsell-related session data
      clearCartSnapshot();
      clearUpsellAddedTimestamp();
      closeModal();
      window.location.assign('/checkout');
    })
    .catch(function() {
      // On error, still go to checkout
      closeModal();
      window.location.assign('/checkout');
    });
  }

  // ========================================
  // BFCACHE AND MOBILE BACK NAVIGATION PROTECTION
  // ========================================

  /**
   * Handle pageshow event - entry point for bfcache detection
   * Called when page is restored from bfcache (e.g., mobile swipe-back)
   * @param {PageTransitionEvent} event
   */
  function handlePageShow(event) {
    if (event.persisted) {
      // Page was restored from bfcache
      handleBFCacheRestoration();
    }
  }

  /**
   * Handle page restoration from bfcache
   * Checks if user navigated to checkout recently and returned via back gesture
   */
  function handleBFCacheRestoration() {
    var navTime = getCheckoutNavigationTimestamp();
    if (navTime && (Date.now() - navTime) < CHECKOUT_DETECTION_WINDOW_MS) {
      // User went to checkout within detection window - this is a back navigation
      clearCheckoutNavigationTimestamp();
      performBFCacheFraudCheck();
    }
  }

  /**
   * Detect navigation type using Performance Navigation API
   * @returns {string} Navigation type: 'navigate', 'reload', 'back_forward', or 'unknown'
   */
  function detectNavigationType() {
    try {
      var entries = performance.getEntriesByType('navigation');
      if (entries && entries.length > 0) {
        return entries[0].type;
      }
    } catch (e) {
      // Performance API not available
    }
    return 'unknown';
  }

  /**
   * Perform fraud check after bfcache restoration
   * Shows checkout return modal if upsell items are present
   */
  function performBFCacheFraudCheck() {
    fetchCurrentCart().then(function(cart) {
      var hasUpsellItems = cart.items.some(function(item) {
        return item.properties && item.properties._fm_upsell === 'true';
      });
      if (hasUpsellItems) {
        showCheckoutReturnModal();
      }
    }).catch(function(error) {
      console.warn('[FM Upsell] BFCache fraud check failed:', error);
    });
  }

  /**
   * Handle visibility change for fraud detection
   * Checks cart state when tab becomes visible after checkout navigation
   */
  function handleVisibilityChangeForFraud() {
    if (document.hidden) return;

    // Only check if there was a checkout navigation timestamp
    var navTime = getCheckoutNavigationTimestamp();
    if (!navTime) return;

    // Check if within detection window
    if ((Date.now() - navTime) < CHECKOUT_DETECTION_WINDOW_MS) {
      // Tab became visible after navigating to checkout - verify cart
      performBFCacheFraudCheck();
    } else {
      // Outside detection window - clear timestamp
      clearCheckoutNavigationTimestamp();
    }
  }

  /**
   * Validate upsell session integrity
   * Removes expired or orphaned upsell items
   */
  function validateUpsellSession() {
    var addedTimestamp = getUpsellAddedTimestamp();
    var snapshot = getCartSnapshot();

    // Check for expired session (upsell items older than MAX_UPSELL_SESSION_MS)
    if (addedTimestamp && (Date.now() - addedTimestamp) > MAX_UPSELL_SESSION_MS) {
      // Session expired - remove upsell items
      removeOrphanedUpsellItems();
      return;
    }

    // Check for orphaned upsells (upsell items exist but no snapshot)
    if (!snapshot) {
      // No snapshot - check if there are orphaned upsell items
      fetchCurrentCart().then(function(cart) {
        var orphanedUpsells = cart.items.filter(function(item) {
          return item.properties && item.properties._fm_upsell === 'true';
        });

        if (orphanedUpsells.length > 0) {
          // Orphaned upsells detected - remove them
          removeOrphanedUpsellItems();
        }
      }).catch(function(error) {
        console.warn('[FM Upsell] Session validation failed:', error);
      });
    }
  }

  /**
   * Remove orphaned upsell items (upsells without valid snapshot)
   */
  function removeOrphanedUpsellItems() {
    fetchCurrentCart().then(function(cart) {
      var upsellItems = cart.items.filter(function(item) {
        return item.properties && item.properties._fm_upsell === 'true';
      });

      if (upsellItems.length === 0) return;

      var updates = {};
      upsellItems.forEach(function(item) {
        updates[item.key] = 0;
      });

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ updates: updates })
      });
    }).then(function() {
      // Clear all upsell-related session data
      clearCartSnapshot();
      clearUpsellAddedTimestamp();
      clearCheckoutNavigationTimestamp();

      // Refresh cart UI
      refreshSidecart();
    }).catch(function(error) {
      console.warn('[FM Upsell] Failed to remove orphaned upsells:', error);
    });
  }

  /**
   * Get checkout navigation timestamp
   * @returns {number|null} Timestamp or null if not set
   */
  function getCheckoutNavigationTimestamp() {
    try {
      var ts = sessionStorage.getItem(CHECKOUT_NAV_TIMESTAMP_KEY);
      return ts ? parseInt(ts, 10) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Set checkout navigation timestamp
   */
  function setCheckoutNavigationTimestamp() {
    try {
      sessionStorage.setItem(CHECKOUT_NAV_TIMESTAMP_KEY, Date.now().toString());
    } catch (e) {
      // sessionStorage not available
    }
  }

  /**
   * Clear checkout navigation timestamp
   */
  function clearCheckoutNavigationTimestamp() {
    try {
      sessionStorage.removeItem(CHECKOUT_NAV_TIMESTAMP_KEY);
    } catch (e) {
      // sessionStorage not available
    }
  }

  /**
   * Get upsell added timestamp
   * @returns {number|null} Timestamp or null if not set
   */
  function getUpsellAddedTimestamp() {
    try {
      var ts = sessionStorage.getItem(UPSELL_ADDED_TIMESTAMP_KEY);
      return ts ? parseInt(ts, 10) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Set upsell added timestamp
   */
  function setUpsellAddedTimestamp() {
    try {
      sessionStorage.setItem(UPSELL_ADDED_TIMESTAMP_KEY, Date.now().toString());
    } catch (e) {
      // sessionStorage not available
    }
  }

  /**
   * Clear upsell added timestamp
   */
  function clearUpsellAddedTimestamp() {
    try {
      sessionStorage.removeItem(UPSELL_ADDED_TIMESTAMP_KEY);
    } catch (e) {
      // sessionStorage not available
    }
  }

  // ========================================
  // FRAUD PREVENTION FUNCTIONS
  // ========================================

  /**
   * Check if modal has already been shown this session
   * @returns {boolean} True if modal was already shown
   */
  function hasModalBeenShown() {
    try {
      return sessionStorage.getItem(MODAL_SHOWN_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }

  /**
   * Mark that modal has been shown this session
   */
  function markModalAsShown() {
    try {
      sessionStorage.setItem(MODAL_SHOWN_KEY, 'true');
    } catch (e) {
      // sessionStorage not available
    }
  }

  /**
   * Set flag indicating user is navigating to checkout
   * Also saves navigation timestamp for bfcache detection
   */
  function setCheckoutIntent() {
    try {
      sessionStorage.setItem(CHECKOUT_INTENT_KEY, 'true');
      // Also save timestamp for bfcache/mobile back navigation detection
      setCheckoutNavigationTimestamp();
    } catch (e) {
      // sessionStorage not available
    }
  }

  /**
   * Detect if user has returned from checkout
   * Uses three methods: checkout intent flag, referrer check, and navigation type with timestamp
   * @returns {boolean} True if user appears to have returned from checkout
   */
  function hasReturnedFromCheckout() {
    try {
      // Method 1: Check if checkout intent was set
      var hadCheckoutIntent = sessionStorage.getItem(CHECKOUT_INTENT_KEY) === 'true';

      // Method 2: Check document.referrer for checkout URL
      var referrerIsCheckout = document.referrer && CHECKOUT_REFERRER_PATTERN.test(document.referrer);

      // Method 3: Check navigation type + timestamp (for bfcache/mobile back navigation)
      var navType = detectNavigationType();
      var navTime = getCheckoutNavigationTimestamp();
      var isBackForwardWithTimestamp = (navType === 'back_forward') &&
                                        navTime &&
                                        (Date.now() - navTime) < CHECKOUT_DETECTION_WINDOW_MS;

      // Clear the intent flag after checking (one-time use)
      sessionStorage.removeItem(CHECKOUT_INTENT_KEY);

      // Note: Don't clear checkout navigation timestamp here - it's used by bfcache handler too

      return hadCheckoutIntent || referrerIsCheckout || isBackForwardWithTimestamp;
    } catch (e) {
      return false;
    }
  }

  /**
   * Remove ALL upsell items from cart (used when returning from checkout)
   * Does not rely on snapshot - finds upsell items by their property marker
   * @returns {Promise} Resolves when items are removed
   */
  function removeAllUpsellItems() {
    return fetchCurrentCart()
      .then(function(cart) {
        var upsellItems = cart.items.filter(function(item) {
          return item.properties && item.properties._fm_upsell === 'true';
        });

        if (upsellItems.length === 0) {
          return Promise.resolve();
        }

        var updates = {};
        upsellItems.forEach(function(item) {
          updates[item.key] = 0;
        });

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ updates: updates })
        })
        .then(function(response) {
          if (!response.ok) throw new Error('Failed to remove upsell items');
          return response.json();
        })
        .then(function() {
          // Clear all upsell-related session data
          clearCartSnapshot();
          clearUpsellAddedTimestamp();
          clearCheckoutNavigationTimestamp();

          console.log('[FM Upsell] Removed upsell items after checkout return - refreshing page');

          // Refresh page to ensure UI is in sync with cart state
          window.location.reload();
        });
      })
      .catch(function(error) {
        console.warn('[FM Upsell] Error removing upsell items:', error);
      });
  }

  /**
   * Handle case where user has returned from checkout
   * Removes upsell items and ensures modal won't show again
   */
  function handleCheckoutReturn() {
    console.log('[FM Upsell] Detected return from checkout - removing upsell items');
    removeAllUpsellItems();
  }

  // ============================================
  // WARNING MODAL FUNCTIONS
  // Cart modification protection
  // ============================================

  /**
   * Initialize warning modal and set up cart modification interception
   */
  function initWarningModal() {
    warningModal = document.getElementById(WARNING_MODAL_ID);
    if (!warningModal) {
      console.log('[FM Upsell] Warning modal element not found');
      return;
    }

    // Set up warning modal events
    setupWarningModalEvents();

    // Set up cart modification interception
    setupCartModificationIntercept();
  }

  /**
   * Set up event listeners for warning modal
   */
  function setupWarningModalEvents() {
    if (!warningModal) return;

    // Cancel button
    var cancelBtn = warningModal.querySelector('[data-fm-warning-cancel]');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', function() {
        hideWarningModal(false);
      });
    }

    // Confirm button
    var confirmBtn = warningModal.querySelector('[data-fm-warning-confirm]');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', function() {
        hideWarningModal(true);
      });
    }
  }

  /**
   * Show warning modal
   * @param {Function} onConfirm - Callback when user confirms
   * @param {Function} onCancel - Callback when user cancels
   */
  function showWarningModal(onConfirm, onCancel) {
    if (!warningModal) {
      // Fallback - pokud modal neexistuje, provést akci
      if (onConfirm) onConfirm();
      return;
    }

    pendingCartAction = {
      confirm: onConfirm || function() {},
      cancel: onCancel || function() {}
    };

    warningModal.hidden = false;
    document.documentElement.classList.add('fm-upsell-warning-open');

    // Focus cancel button pro přístupnost
    var cancelBtn = warningModal.querySelector('[data-fm-warning-cancel]');
    if (cancelBtn) {
      setTimeout(function() { cancelBtn.focus(); }, 100);
    }
  }

  /**
   * Hide warning modal
   * @param {Boolean} confirmed - True if user confirmed, false if cancelled
   */
  function hideWarningModal(confirmed) {
    if (!warningModal) return;

    warningModal.hidden = true;
    document.documentElement.classList.remove('fm-upsell-warning-open');

    if (pendingCartAction) {
      if (confirmed && pendingCartAction.confirm) {
        pendingCartAction.confirm();
      } else if (!confirmed && pendingCartAction.cancel) {
        pendingCartAction.cancel();
      }
    }

    pendingCartAction = null;
  }

  /**
   * Show checkout return modal
   * Displayed when user returns from checkout with upsell items in cart
   */
  function showCheckoutReturnModal() {
    // Nejprve zkontrolovat zda jsou v košíku upsell položky
    fetchCurrentCart()
      .then(function(cart) {
        var hasUpsellItems = cart.items.some(function(item) {
          return item.properties && item.properties._fm_upsell === 'true';
        });

        if (!hasUpsellItems) {
          // Žádné upsell položky - nevolat modal, pokračovat normálně
          initCartProtection();
          initWarningModal();
          return;
        }

        // Jsou upsell položky - zobrazit modal
        warningModal = document.getElementById(WARNING_MODAL_ID);
        if (!warningModal) {
          // Fallback - redirect to checkout
          window.location.href = '/checkout';
          return;
        }

        // Přidat třídu pro zelené tlačítko
        warningModal.classList.add('fm-upsell-warning--return');

        // Načíst texty z data atributů
        var titleEl = warningModal.querySelector('.fm-upsell-warning__title');
        var messageEl = warningModal.querySelector('.fm-upsell-warning__message');
        var cancelBtn = warningModal.querySelector('[data-fm-warning-cancel]');
        var confirmBtn = warningModal.querySelector('[data-fm-warning-confirm]');

        if (titleEl && warningModal.dataset.returnTitle) {
          titleEl.textContent = warningModal.dataset.returnTitle;
        }
        if (messageEl && warningModal.dataset.returnMessage) {
          messageEl.textContent = warningModal.dataset.returnMessage;
        }
        if (cancelBtn && warningModal.dataset.returnRemove) {
          cancelBtn.textContent = warningModal.dataset.returnRemove;
        }
        if (confirmBtn && warningModal.dataset.returnCheckout) {
          confirmBtn.textContent = warningModal.dataset.returnCheckout;
        }

        // Nastavit handlery
        setupWarningModalEvents();

        // Zobrazit modal
        showWarningModal(
          function() {
            // "Zpět do pokladny" = redirect na checkout
            window.location.href = '/checkout';
          },
          function() {
            // "Odebrat nabídku" = smazat upsell položky
            removeAllUpsellItems();
          }
        );
      })
      .catch(function(error) {
        console.warn('[FM Upsell] Error checking cart for upsell items:', error);
        // V případě chyby pokračovat normálně
        initCartProtection();
        initWarningModal();
      });
  }

  /**
   * Set up interception for cart modifications
   */
  function setupCartModificationIntercept() {
    // Patch fetch pro AJAX cart operace
    patchFetchForCartProtection();

    // Intercept form submissions
    document.addEventListener('submit', interceptCartFormSubmit, true);

    // Intercept clicks on remove buttons and quantity buttons
    document.addEventListener('click', interceptCartClick, true);

    // Intercept quantity input changes
    document.addEventListener('change', interceptQuantityChange, true);
  }

  /**
   * Patch window.fetch to intercept cart modification requests
   */
  function patchFetchForCartProtection() {
    window.fetch = function(url, options) {
      var urlStr = typeof url === 'string' ? url : (url.url || '');

      // Check if this is a cart modification request
      if (isCartModificationRequest(urlStr)) {
        var snapshot = getCartSnapshot();

        // Pokud není snapshot (žádné upsell položky), pokračovat normálně
        if (!snapshot) {
          return originalFetch.apply(this, arguments);
        }

        // Kontrola zda tato operace odstraní původní položky
        return checkIfWillInvalidateUpsells(urlStr, options)
          .then(function(willInvalidate) {
            if (willInvalidate) {
              // Vrátit Promise, která čeká na uživatelovu odpověď
              return new Promise(function(resolve, reject) {
                showWarningModal(
                  function() {
                    // Confirmed - provést původní fetch
                    originalFetch(url, options)
                      .then(resolve)
                      .catch(reject);
                  },
                  function() {
                    // Cancelled - vrátit fake úspěšnou odpověď
                    resolve(new Response(JSON.stringify({ items: [] }), {
                      status: 200,
                      headers: { 'Content-Type': 'application/json' }
                    }));
                  }
                );
              });
            } else {
              return originalFetch(url, options);
            }
          });
      }

      return originalFetch.apply(this, arguments);
    };
  }

  /**
   * Check if URL is a cart modification request
   */
  function isCartModificationRequest(url) {
    return url.indexOf('/cart/change') !== -1 ||
           url.indexOf('/cart/update') !== -1;
  }

  /**
   * Check if the cart operation will remove items that upsells depend on
   */
  function checkIfWillInvalidateUpsells(url, options) {
    return new Promise(function(resolve) {
      var snapshot = getCartSnapshot();
      if (!snapshot || !snapshot.originalVariantIds || snapshot.originalVariantIds.length === 0) {
        resolve(false);
        return;
      }

      // Parsovat body requestu
      var body = null;
      if (options && options.body) {
        if (typeof options.body === 'string') {
          try {
            body = JSON.parse(options.body);
          } catch (e) {
            // Pokusit se parsovat jako form data
            body = parseFormData(options.body);
          }
        } else if (options.body instanceof FormData) {
          body = formDataToObject(options.body);
        }
      }

      if (!body) {
        resolve(false);
        return;
      }

      // Pro /cart/change.js - kontrola konkrétní položky
      if (url.indexOf('/cart/change') !== -1) {
        var targetId = body.id || body.line;
        var newQuantity = parseInt(body.quantity, 10);

        // Pokud se snižuje na 0, je to odstranění
        if (newQuantity === 0) {
          // Zjistit zda je to původní položka
          fetchCurrentCart().then(function(cart) {
            var item = cart.items.find(function(i) {
              return i.key === targetId || String(i.variant_id) === String(targetId);
            });

            if (item && snapshot.originalVariantIds.indexOf(item.variant_id) !== -1) {
              // Toto je původní položka - upozornit
              resolve(true);
            } else {
              resolve(false);
            }
          }).catch(function() {
            resolve(false);
          });
          return;
        }
      }

      // Pro /cart/update.js - kontrola updates objektu
      if (url.indexOf('/cart/update') !== -1 && body.updates) {
        // updates může být objekt {variantId: quantity} nebo pole
        var updatesObj = body.updates;
        var hasRemoval = false;

        if (typeof updatesObj === 'object') {
          for (var key in updatesObj) {
            if (updatesObj.hasOwnProperty(key)) {
              var qty = parseInt(updatesObj[key], 10);
              var variantId = parseInt(key, 10);

              if (qty === 0 && snapshot.originalVariantIds.indexOf(variantId) !== -1) {
                hasRemoval = true;
                break;
              }
            }
          }
        }

        resolve(hasRemoval);
        return;
      }

      resolve(false);
    });
  }

  /**
   * Parse URL-encoded form data string
   */
  function parseFormData(str) {
    var result = {};
    var pairs = str.split('&');
    for (var i = 0; i < pairs.length; i++) {
      var pair = pairs[i].split('=');
      if (pair.length === 2) {
        result[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
      }
    }
    return result;
  }

  /**
   * Convert FormData to plain object
   */
  function formDataToObject(formData) {
    var result = {};
    formData.forEach(function(value, key) {
      result[key] = value;
    });
    return result;
  }

  /**
   * Intercept cart form submissions
   */
  function interceptCartFormSubmit(e) {
    var form = e.target;
    if (!form || !form.matches) return;

    // Kontrola zda je to cart form (ne checkout)
    if (!form.matches('form[action*="/cart"]')) return;
    if (form.action && form.action.indexOf('/checkout') !== -1) return;

    var snapshot = getCartSnapshot();
    if (!snapshot) return;

    // Kontrola zda form obsahuje odstranění původních položek
    var willInvalidate = checkFormForInvalidation(form, snapshot);

    if (willInvalidate) {
      e.preventDefault();
      e.stopPropagation();

      showWarningModal(
        function() {
          // Confirmed - odeslat form
          form.submit();
        },
        function() {
          // Cancelled - nic nedělat
        }
      );
    }
  }

  /**
   * Check if form submission will invalidate upsells
   */
  function checkFormForInvalidation(form, snapshot) {
    var inputs = form.querySelectorAll('input[name^="updates"]');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var value = parseInt(input.value, 10);

      if (value === 0) {
        // Zkusit zjistit variant ID z input name nebo parent elementu
        var match = input.name.match(/updates\[(\d+)\]/);
        if (match) {
          var variantId = parseInt(match[1], 10);
          if (snapshot.originalVariantIds.indexOf(variantId) !== -1) {
            return true;
          }
        }
      }
    }
    return false;
  }

  /**
   * Intercept clicks on cart remove buttons
   */
  function interceptCartClick(e) {
    var target = e.target.closest('[data-cart-remove], [data-remove-item], .cart-item__remove, .remove-item, a[href*="/cart/change?quantity=0"]');
    if (!target) return;

    // Pokud už bylo potvrzeno, nepřerušovat
    if (target.dataset.fmWarningConfirmed === 'true') {
      delete target.dataset.fmWarningConfirmed;
      return;
    }

    var snapshot = getCartSnapshot();
    if (!snapshot) return;

    // Zjistit variant ID z elementu nebo jeho parent
    var variantId = getVariantIdFromElement(target);

    if (variantId && snapshot.originalVariantIds.indexOf(variantId) !== -1) {
      e.preventDefault();
      e.stopPropagation();

      showWarningModal(
        function() {
          // Confirmed - provést click znovu bez interceptu
          target.dataset.fmWarningConfirmed = 'true';
          target.click();
        },
        function() {
          // Cancelled - nic nedělat
        }
      );
    }
  }

  /**
   * Intercept quantity input changes
   */
  function interceptQuantityChange(e) {
    var input = e.target;
    if (!input || !input.matches) return;

    // Kontrola zda je to quantity input
    if (!input.matches('input[name="quantity"], input[name^="updates"], input[data-quantity-input]')) return;

    var snapshot = getCartSnapshot();
    if (!snapshot) return;

    var newValue = parseInt(input.value, 10);
    if (newValue !== 0) return; // Pouze kontrolujeme odstranění

    var variantId = getVariantIdFromElement(input);

    if (variantId && snapshot.originalVariantIds.indexOf(variantId) !== -1) {
      e.preventDefault();
      e.stopPropagation();

      // Vrátit původní hodnotu
      input.value = input.dataset.previousValue || 1;

      showWarningModal(
        function() {
          // Confirmed - nastavit hodnotu na 0
          input.value = 0;
          // Trigger change event pro theme
          input.dispatchEvent(new Event('change', { bubbles: true }));
        },
        function() {
          // Cancelled - hodnota už je vrácena
        }
      );
    }
  }

  /**
   * Get variant ID from element (tries multiple strategies)
   */
  function getVariantIdFromElement(element) {
    // Strategy 1: data-variant-id attribute
    var variantId = element.dataset.variantId || element.getAttribute('data-variant-id');
    if (variantId) return parseInt(variantId, 10);

    // Strategy 2: Find in parent cart item
    var cartItem = element.closest('[data-variant-id], [data-line-item-variant-id], .cart-item, .cart__item');
    if (cartItem) {
      variantId = cartItem.dataset.variantId || cartItem.getAttribute('data-variant-id') ||
                  cartItem.dataset.lineItemVariantId;
      if (variantId) return parseInt(variantId, 10);
    }

    // Strategy 3: Parse from href (for remove links)
    var href = element.getAttribute('href');
    if (href) {
      var match = href.match(/id=(\d+)/);
      if (match) return parseInt(match[1], 10);
    }

    // Strategy 4: Find hidden input in parent form
    var form = element.closest('form');
    if (form) {
      var hiddenId = form.querySelector('input[name="id"]');
      if (hiddenId) return parseInt(hiddenId.value, 10);
    }

    // Strategy 5: Parse from input name
    var inputName = element.name || '';
    var nameMatch = inputName.match(/updates\[(\d+)\]/);
    if (nameMatch) return parseInt(nameMatch[1], 10);

    return null;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
